/**
 * The plumbing shared by the app's two `<canvas>` drawings — the dotted world
 * map behind every screen (`AppBackground`) and the orbital globe on the key
 * gate (`GlobeHero`).
 *
 * Neither of them is about canvas mechanics; they are about a map and a globe.
 * What is here is everything that was identical between them and had started to
 * drift apart: the retina backing store, the resize hook, and the two constants
 * every canvas in a browser ends up redefining.
 *
 * No React. These are called *from* effects, not instead of them — see
 * `observeCanvasResize`.
 */

export const TAU = Math.PI * 2;

/**
 * Retina backing stores are capped here.
 *
 * Without any scaling the drawings come out at a third of a modern iPhone's
 * resolution and read as soft grey smudges rather than as points of light. The
 * cap is because the buffer cost is quadratic and nothing above 3 is visible on
 * a 0.6px dot.
 */
export const MAX_PIXEL_RATIO = 3;

/** A canvas's CSS box plus the pixel ratio its backing store is sized for. */
export interface CanvasBox {
  width: number;
  height: number;
  ratio: number;
}

/**
 * Reads the canvas's CSS box and the (capped) device pixel ratio.
 *
 * `null` when the element has no size yet — laid out but not measured, or
 * `display: none`. There is nothing to draw into and, more to the point, no
 * safe backing-store size to allocate.
 *
 * This is a **layout read**: it flushes pending style and layout work. Fine
 * once per resize, which is when the answer can actually change; a caller
 * running at 60fps should measure on resize and hold on to the result rather
 * than asking here every frame.
 */
export function measureCanvas(canvas: HTMLCanvasElement): CanvasBox | null {
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (width <= 0 || height <= 0) return null;
  return { width, height, ratio: Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO) };
}

/**
 * Matches the backing store to the CSS box and the device pixel ratio, sets the
 * transform so the caller can draw in CSS pixels, and clears the frame.
 *
 * Pass `box` to reuse a measurement taken on resize; omit it and the canvas is
 * measured here.
 *
 * The buffer size is only assigned when it has actually changed. Writing
 * `canvas.width` reallocates the buffer even when the value is identical, and
 * doing that at 60fps is a per-frame allocation for no change on screen.
 */
export function fitCanvas(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  box?: CanvasBox,
): CanvasBox | null {
  const measured = box ?? measureCanvas(canvas);
  if (!measured) return null;

  const bufferWidth = Math.round(measured.width * measured.ratio);
  const bufferHeight = Math.round(measured.height * measured.ratio);
  if (canvas.width !== bufferWidth || canvas.height !== bufferHeight) {
    canvas.width = bufferWidth;
    canvas.height = bufferHeight;
  }
  context.setTransform(measured.ratio, 0, 0, measured.ratio, 0, 0);
  context.clearRect(0, 0, measured.width, measured.height);
  return measured;
}

/**
 * Calls `onResize` whenever the element's box changes. Returns the teardown.
 *
 * A plain function rather than a `useX` hook so this file stays React-free and
 * lives with the drawing code it belongs to. It is shaped to be the whole body
 * of an effect's cleanup: `return observeCanvasResize(element, redraw)`.
 *
 * ResizeObserver rather than a `resize` listener: on iOS the viewport changes
 * size without a resize event the app can rely on (the URL bar collapsing, the
 * software keyboard, a Guided Access rotation), and the observer reports the box
 * we actually draw into instead of the window's idea of it. The listener is the
 * fallback for an engine without ResizeObserver, where a window-level resize is
 * the only signal there is.
 *
 * Note that `observe` fires once immediately with the current size, so a caller
 * that also draws before subscribing will draw twice on mount. That is the
 * intended order: the fallback path fires nothing until the window changes, so
 * the first paint cannot be left to the subscription.
 */
export function observeCanvasResize(element: Element, onResize: () => void): () => void {
  if (typeof ResizeObserver !== 'function') {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }
  const observer = new ResizeObserver(onResize);
  observer.observe(element);
  return () => observer.disconnect();
}
